import Chart from "chart.js";
import moment from "moment-timezone";

const CARD_WIDTH = 260;
const CARD_PADDING = 10;
const LINE_HEIGHT = 16;
const TITLE_SIZE = 12;
const BODY_SIZE = 11;
const MAX_NOTE_LINES = 8;

function xForDate(chart, iso) {
	const xAxis = chart.scales && (chart.scales["x-axis-0"] || chart.scales.x);
	if (!xAxis) {
		return null;
	}
	const labels = chart.data.labels || [];
	const target = moment(iso).tz("America/Phoenix");
	if (!target.isValid()) {
		return null;
	}
	const targetDay = target.format("YYYY-MM-DD");
	let idx = labels.findIndex((label) => String(label).slice(0, 10) === targetDay);
	if (idx < 0 && labels.length > 0) {
		const times = labels.map((label) => moment(label).valueOf());
		const t = target.valueOf();
		if (t <= times[0]) {
			idx = 0;
		} else if (t >= times[times.length - 1]) {
			idx = times.length - 1;
		} else {
			for (let i = 0; i < times.length - 1; i++) {
				if (t >= times[i] && t <= times[i + 1]) {
					const span = times[i + 1] - times[i] || 1;
					const left = xAxis.getPixelForTick(i);
					const right = xAxis.getPixelForTick(i + 1);
					return left + ((t - times[i]) / span) * (right - left);
				}
			}
		}
	}
	if (idx < 0) {
		return null;
	}
	return typeof xAxis.getPixelForTick === "function" ? xAxis.getPixelForTick(idx) : xAxis.getPixelForValue(idx);
}

export function wrapCanvasText(ctx, text, maxWidth) {
	const words = String(text || "").replace(/\s+/g, " ").trim().split(" ");
	if (!words[0]) {
		return [];
	}
	const lines = [];
	let current = "";
	words.forEach((word) => {
		const attempt = current ? `${current} ${word}` : word;
		if (ctx.measureText(attempt).width <= maxWidth) {
			current = attempt;
			return;
		}
		if (current) {
			lines.push(current);
		}
		if (ctx.measureText(word).width <= maxWidth) {
			current = word;
			return;
		}
		let chunk = "";
		for (let i = 0; i < word.length; i++) {
			const next = chunk + word[i];
			if (chunk && ctx.measureText(next).width > maxWidth) {
				lines.push(chunk);
				chunk = word[i];
			} else {
				chunk = next;
			}
		}
		current = chunk;
	});
	if (current) {
		lines.push(current);
	}
	return lines;
}

function markerCardLines(ctx, marker, maxWidth) {
	const when = moment(marker.created).tz("America/Phoenix");
	const whenText = when.isValid() ? when.format("ddd MMM D h:mm A") : "";
	ctx.font = `bold ${TITLE_SIZE}px sans-serif`;
	const title = wrapCanvasText(ctx, marker.engineer_name || "Field Engineer", maxWidth);
	ctx.font = `${BODY_SIZE}px sans-serif`;
	const meta = wrapCanvasText(ctx, [marker.actor_name || "Admin", whenText].filter(Boolean).join(" · "), maxWidth);
	let note = wrapCanvasText(ctx, marker.notes || "", maxWidth);
	if (note.length > MAX_NOTE_LINES) {
		note = note.slice(0, MAX_NOTE_LINES);
		const last = note[note.length - 1];
		note[note.length - 1] = last.length > 3 ? `${last.slice(0, last.length - 3)}...` : "...";
	}
	return {title, meta, note};
}

function drawContactCard(ctx, chart, marker, x, yAxis) {
	const host = chart.chart || chart;
	const maxWidth = CARD_WIDTH - CARD_PADDING * 2;
	const card = markerCardLines(ctx, marker, maxWidth);
	const lines = card.title.length + card.meta.length + card.note.length;
	const height = CARD_PADDING * 2 + Math.max(1, lines) * LINE_HEIGHT;
	const width = Math.min(CARD_WIDTH, (host.width || 300) - 16);
	let boxX = x + 10;
	if (boxX + width > (host.width || 300) - 8) {
		boxX = Math.max(8, x - width - 10);
	}
	let boxY = yAxis.top + 8;
	if (boxY + height > yAxis.bottom - 8) {
		boxY = Math.max(yAxis.top + 8, yAxis.bottom - height - 8);
	}
	ctx.save();
	ctx.fillStyle = "rgba(33, 37, 41, 0.94)";
	ctx.strokeStyle = "rgba(255, 255, 255, 0.2)";
	ctx.lineWidth = 1;
	ctx.fillRect(boxX, boxY, width, height);
	ctx.strokeRect(boxX, boxY, width, height);
	let textY = boxY + CARD_PADDING + 12;
	const textX = boxX + CARD_PADDING;
	ctx.fillStyle = "#fff";
	ctx.font = `bold ${TITLE_SIZE}px sans-serif`;
	card.title.forEach((line) => {
		ctx.fillText(line, textX, textY, maxWidth);
		textY += LINE_HEIGHT;
	});
	ctx.font = `${BODY_SIZE}px sans-serif`;
	ctx.fillStyle = "rgba(255, 255, 255, 0.8)";
	card.meta.forEach((line) => {
		ctx.fillText(line, textX, textY, maxWidth);
		textY += LINE_HEIGHT;
	});
	ctx.fillStyle = "#fff";
	card.note.forEach((line) => {
		ctx.fillText(line, textX, textY, maxWidth);
		textY += LINE_HEIGHT;
	});
	ctx.restore();
}

export function registerFeContactLinePlugin(ChartLib) {
	const targets = [ChartLib, Chart];
	if (typeof window !== "undefined" && window.Chart && window.Chart !== ChartLib) {
		targets.push(window.Chart);
	}
	targets.forEach((ChartTarget) => {
		if (!ChartTarget || !ChartTarget.pluginService || ChartTarget.__feContactLinesRegistered) {
			return;
		}
		ChartTarget.pluginService.register({
			id: "feContactLines",
			afterEvent(chart, event) {
				const markers = chart.options.feContactMarkers || [];
				if (!event || !markers.length) {
					return;
				}
				if (event.type !== "mousemove" && event.type !== "mouseout") {
					return;
				}
				if (event.type === "mouseout") {
					chart.feContactHover = null;
					chart.draw();
					return;
				}
				let nearest = null;
				let best = 12;
				markers.forEach((marker) => {
					const x = xForDate(chart, marker.created || marker.date_contacted);
					if (x == null) {
						return;
					}
					const dist = Math.abs(event.x - x);
					if (dist < best) {
						best = dist;
						nearest = {marker, x};
					}
				});
				const next = nearest ? nearest.marker.pkey : null;
				const prev = chart.feContactHover ? chart.feContactHover.pkey : null;
				chart.feContactHover = nearest ? nearest.marker : null;
				chart.feContactHoverX = nearest ? nearest.x : null;
				if (next !== prev) {
					chart.draw();
				}
			},
			afterDraw(chart) {
				const markers = chart.options.feContactMarkers || [];
				if (!markers.length) {
					return;
				}
				const yAxis = chart.scales && (chart.scales["y-axis-0"] || chart.scales.y);
				if (!yAxis) {
					return;
				}
				const host = chart.chart || chart;
				const ctx = host.ctx;
				if (!ctx) {
					return;
				}
				markers.forEach((marker) => {
					const x = xForDate(chart, marker.created || marker.date_contacted);
					if (x == null) {
						return;
					}
					ctx.save();
					ctx.beginPath();
					ctx.strokeStyle = "rgba(90, 90, 90, 0.85)";
					ctx.lineWidth = 1;
					ctx.setLineDash([4, 3]);
					ctx.moveTo(x, yAxis.top);
					ctx.lineTo(x, yAxis.bottom);
					ctx.stroke();
					ctx.restore();
				});
				if (chart.feContactHover) {
					const x = chart.feContactHoverX || xForDate(chart, chart.feContactHover.created);
					if (x == null) {
						return;
					}
					drawContactCard(ctx, chart, chart.feContactHover, x, yAxis);
				}
			},
		});
		ChartTarget.__feContactLinesRegistered = true;
	});
}
